// Tesla Fleet API OAuth Authentication

const CLIENT_ID = 'bb98a96f-c840-40e1-ada5-118d3c80305c';
const CLIENT_SECRET = 'ta-secret.1Hz&uXh0i9o&Egj$';
const REDIRECT_URI = window.location.hostname === 'localhost'
    ? 'http://localhost:3000/q'
    : 'https://divy.live/q';

// Tesla Fleet API requires these scopes for vehicle data
const SCOPES = [
    'openid',
    'vehicle_device_data',
    'vehicle_location',
].join(' ');

const TESLA_AUTH_URL = 'https://auth.tesla.com/oauth2/v3';
const TESLA_TOKEN_KEY = 'tesla_tokens';

// Generate random state for CSRF protection
function generateState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

// Initiate Tesla OAuth login
export function initiateTeslaLogin() {
    const state = generateState();
    sessionStorage.setItem('tesla_oauth_state', state);

    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        response_type: 'code',
        scope: SCOPES,
        state: state,
    });

    window.location.href = `${TESLA_AUTH_URL}/authorize?${params.toString()}`;
}

// Handle OAuth callback - exchange code for tokens
export async function handleTeslaCallback(code, state) {
    // Verify state
    const savedState = sessionStorage.getItem('tesla_oauth_state');
    if (state !== savedState) {
        throw new Error('Invalid state parameter');
    }
    sessionStorage.removeItem('tesla_oauth_state');

    const response = await fetch(`${TESLA_AUTH_URL}/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            code: code,
            redirect_uri: REDIRECT_URI,
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('Tesla token error:', error);
        throw new Error(error.error_description || 'Tesla token exchange failed');
    }

    const tokens = await response.json();

    // Store tokens
    const tokenData = {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + tokens.expires_in * 1000,
    };
    localStorage.setItem(TESLA_TOKEN_KEY, JSON.stringify(tokenData));

    return tokenData;
}

// Get stored Tesla tokens
export function getTeslaTokens() {
    const stored = localStorage.getItem(TESLA_TOKEN_KEY);
    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

// Check if Tesla is authenticated
export function isTeslaAuthenticated() {
    const tokens = getTeslaTokens();
    if (!tokens) return false;
    // Check if token is not expired (with 5 min buffer)
    return tokens.expiresAt > Date.now() + 5 * 60 * 1000;
}

// Refresh Tesla access token
export async function refreshTeslaToken() {
    const tokens = getTeslaTokens();
    if (!tokens?.refreshToken) {
        throw new Error('No refresh token available');
    }

    const response = await fetch(`${TESLA_AUTH_URL}/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            refresh_token: tokens.refreshToken,
        }),
    });

    if (!response.ok) {
        localStorage.removeItem(TESLA_TOKEN_KEY);
        throw new Error('Tesla token refresh failed');
    }

    const newTokens = await response.json();

    const tokenData = {
        accessToken: newTokens.access_token,
        refreshToken: newTokens.refresh_token || tokens.refreshToken,
        expiresAt: Date.now() + newTokens.expires_in * 1000,
    };
    localStorage.setItem(TESLA_TOKEN_KEY, JSON.stringify(tokenData));

    return tokenData.accessToken;
}

// Get valid access token (refresh if needed)
export async function getTeslaAccessToken() {
    const tokens = getTeslaTokens();
    if (!tokens) return null;

    // Refresh if expired or about to expire
    if (tokens.expiresAt <= Date.now() + 5 * 60 * 1000) {
        try {
            return await refreshTeslaToken();
        } catch (error) {
            console.error('Failed to refresh Tesla token:', error);
            return null;
        }
    }

    return tokens.accessToken;
}

// Clear Tesla tokens
export function clearTeslaTokens() {
    localStorage.removeItem(TESLA_TOKEN_KEY);
}

// Register domain with Tesla Partner API (one-time setup)
// Server handles getting the partner token via client_credentials
export async function registerTeslaDomain() {
    const response = await fetch('/api/tesla-register', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || data.details?.error_description || `Registration failed: ${response.status}`);
    }

    return data;
}

